import React, { useEffect, useState } from "react";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { actions, reducer, sliceKey } from "./slice";
import { useDispatch, useSelector } from "react-redux";
import { raisedCorrectionSaga } from "./saga";
import {
  Modal,
  Checkbox,
  Button,
  Input,
  Typography,
  Space,
  message,
  notification,
  Alert,
  Spin,
} from "antd";
import {
  CloseOutlined,
  SendOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import * as selectors from "./selectors";

const { TextArea } = Input;
const { Text } = Typography;

const correctionOptions = [
  { key: "report", label: "Correction in Report" },
  { key: "acceptanceForm", label: "Acceptance Form" },
  { key: "labellingForm", label: "Labelling Form" },
];

function RaiseCorrectionRequest({ id, onOpen, onClose, orderId, fetchUpdatedData }) {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: raisedCorrectionSaga });
  const dispatch = useDispatch();
  
  const [requestCorrections, setRequestCorrections] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [isRequestRaising, setIsRequestRaising] = useState(false);
  const correctionData = useSelector(selectors.selectCorrectionData);
  const [fetchingCorrectionData, setFetchingCorrectionData] = useState(false);

  useEffect(() => {
    setFetchingCorrectionData(true);
    dispatch(
      actions.fetchCorrectionData({
        id,
        onSuccess: () => {
          setFetchingCorrectionData(false);
        },
        onFailure: () => {
          setFetchingCorrectionData(false);
        },
      })
    );
  }, [dispatch, id]);

  const handleRequestCorrectionChange = (key, checked) => {
    setRequestCorrections((prev) => {
      if (checked) {
        return [...prev, key];
      } else {
        return prev.filter((item) => item !== key);
      }
    });
  };

  const handleSubmit = () => {
    // Handle form submission
    if (requestCorrections.length === 0) {
      message.warning("Please select at least one correction type.");
      return;
    }
    if (remarks.trim() === "") {
      message.warning("Please enter remarks.");
      return;
    }

    const postData = {
      id: id,
      requestCorrections: requestCorrections,
      remark: remarks,
    };

    setIsRequestRaising(true);
    dispatch(
      actions.raiseCorrectionRequest({
        ...postData,
        onSuccess: () => {
          setIsRequestRaising(false);
          onClose();
          notification.success({
            message: "Success",
            description: "Correction request raised successfully.",
          });
          fetchUpdatedData();
        },
        onFailure: (error) => {
          setIsRequestRaising(false);
          notification.error({
            message: "Error",
            description:
              error?.message || "Failed to raise correction request.",
          });
        },
      })
    );
  };

  const handleCancel = () => {
    setRequestCorrections([]);
    setRemarks("");
    onClose();
  };

  const last =
    correctionData?.underDisputes?.[correctionData.underDisputes.length - 1];
  const isAlreadyRaised = last?.isResolved === false;

  return (
    <Modal
      visible={onOpen}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isRequestRaising}>
          Cancel
        </Button>,
        <Button
          key="raiseCorrectionRequest"
          type="primary"
          onClick={handleSubmit}
          loading={isRequestRaising}
          icon={!isRequestRaising ? <SendOutlined /> : null}
          disabled={isAlreadyRaised}
        >
          Raise Correction Request
        </Button>,
      ]}
      width={900}
      closeIcon={
        <CloseOutlined style={{ fontSize: "16px", color: "#6d798d" }} />
      }
      title={
        <div
          style={{
            fontWeight: 600,
            fontSize: "18px",
            color: "#262626",
            lineHeight: "24px",
          }}
        >
          Raise Correction - Order #{orderId}
        </div>
      }
      bodyStyle={{
        padding: "24px",
        maxHeight: "600px",
        overflowY: "auto",
      }}
    >
      {fetchingCorrectionData ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {isAlreadyRaised && (
            <Alert
              message={
                <span style={{ color: "#ad8b00" }}>
                  Pending Correction Exists.
                </span>
              }
              description={
                <span style={{ color: "#ad8b00" }}>
                  You must close the existing pending correction before raising
                  a new one.
                </span>
              }
              type="warning"
              showIcon
              icon={<WarningOutlined style={{ color: "#ad8b00" }} />}
            />
          )}
          {/* Correction Types Section */}
          <div>
            <div style={{ marginBottom: "16px" }}>
              <Text
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#262626",
                }}
              >
                Correction Types <span style={{ color: "#ff4d4f" }}>*</span>
              </Text>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <Text
                style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#6d798d",
                  lineHeight: "22px",
                }}
              >
                Select all types of corrections needed for this order:
              </Text>
            </div>

            <Space direction="vertical" size={12}>
              {correctionOptions.map(({ key, label }) => (
                <Checkbox
                  key={key}
                  checked={requestCorrections.includes(key)}
                  onChange={(e) =>
                    handleRequestCorrectionChange(key, e.target.checked)
                  }
                >
                  <Text style={{ fontSize: 16, color: "#262626" }}>
                    {label}
                  </Text>
                </Checkbox>
              ))}
            </Space>
          </div>

          {/* Remarks Section */}
          <div>
            <div style={{ marginBottom: "16px" }}>
              <Text
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#262626",
                }}
              >
                Remarks <span style={{ color: "#ff4d4f" }}>*</span>
              </Text>
            </div>

            <TextArea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Please provide detailed remarks about the correction needed..."
              rows={6}
              style={{
                fontSize: "16px",
                color: "#6d798d",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
              }}
            />

            <div style={{ marginTop: "8px" }}>
              <Text
                style={{
                  fontSize: "14px",
                  color: "#6d798d",
                }}
              >
                Be specific about what needs to be corrected and why.
              </Text>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default RaiseCorrectionRequest;
